import { BehaviorSubject, Subject } from 'rxjs';

// Tracks local fullscreen per scope (chart/symbol layout).
// We cannot replace this with a single boolean:
// if scope A exits fullscreen while scope B is still fullscreen,
// global UI should stay in fullscreen mode.
const fullscreenByScope = new Map<string, true>();

// Manual "fullscreen all charts" toggle (toolbar/floating button).
const globalWorkspaceFullscreen$ = new BehaviorSubject<boolean>(false);

// Effective fullscreen used by UI (header/toolbars/side controls):
// true when either global fullscreen is enabled or any local scope is fullscreen.
const workspaceFullscreen$ = new BehaviorSubject<boolean>(false);

// One-shot "exit fullscreen now" signal (e.g. Escape key).
// Consumers use it to restore local maximized tabsets.
const workspaceFullscreenExit$ = new Subject<void>();

const syncFullscreenState = () => {
  workspaceFullscreen$.next(
    globalWorkspaceFullscreen$.getValue() || fullscreenByScope.size > 0
  );
};

export const getWorkspaceFullscreen$ = () => workspaceFullscreen$.asObservable();
export const getGlobalWorkspaceFullscreen$ = () =>
  globalWorkspaceFullscreen$.asObservable();
export const getWorkspaceFullscreenExit$ = () =>
  workspaceFullscreenExit$.asObservable();

export const isWorkspaceFullscreen = () => workspaceFullscreen$.getValue();
export const isGlobalWorkspaceFullscreen = () =>
  globalWorkspaceFullscreen$.getValue();

export const setGlobalWorkspaceFullscreen = (fullscreen: boolean) => {
  globalWorkspaceFullscreen$.next(fullscreen);
  syncFullscreenState();
};

// Unified exit path:
// - disables global fullscreen
// - broadcasts "restore local maximize" for layout consumers
export const exitWorkspaceFullscreen = () => {
  setGlobalWorkspaceFullscreen(false);
  workspaceFullscreenExit$.next();
};

export const setWorkspaceFullscreenScope = (
  scopeId: string,
  fullscreen: boolean
) => {
  if (fullscreen) {
    fullscreenByScope.set(scopeId, true);
  } else {
    fullscreenByScope.delete(scopeId);
  }

  syncFullscreenState();
};

export const clearWorkspaceFullscreenScope = (scopeId: string) => {
  fullscreenByScope.delete(scopeId);
  syncFullscreenState();
};
